// 画像管理サービスの実装
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll, getMetadata } from 'firebase/storage';
import { storage } from '../firebase/config';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

// 画像・動画メタデータの型定義
export interface ImageMetadata {
  url: string;
  name: string;
  size: number;
  uploadedAt: Date;
  fullPath: string;
  contentType: string;
  isVideo?: boolean;
  processingStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  processingError?: string;
}

export class ImageService {
  private static ffmpeg: FFmpeg | null = null;

  // FFmpegの初期化
  private static async initFFmpeg(): Promise<FFmpeg> {
    if (this.ffmpeg) {
      return this.ffmpeg;
    }

    const ffmpeg = new FFmpeg();
    
    // FFmpegのロード
    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });

    this.ffmpeg = ffmpeg;
    return ffmpeg;
  }

  // 画像をWebP形式に変換（リサイズ機能付き）
  static async convertToWebP(file: File, quality: number = 0.85, maxWidth: number = 1920): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      const objectUrl = URL.createObjectURL(file);
      
      img.onload = () => {
        // 画像サイズを計算（最大幅を超える場合はリサイズ）
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          const ratio = maxWidth / width;
          width = maxWidth;
          height = height * ratio;
        }
        
        // キャンバスに画像サイズを設定
        canvas.width = width;
        canvas.height = height;
        
        // 画像を描画
        ctx?.drawImage(img, 0, 0, width, height);
        
        // WebPに変換
        canvas.toBlob(
          (blob) => {
            // メモリリーク防止
            URL.revokeObjectURL(objectUrl);
            
            if (blob) {
              // 元のファイル名から拡張子を除去してwebpを追加
              const originalName = file.name.replace(/\.[^/.]+$/, '');
              const webpFile = new File([blob], `${originalName}.webp`, {
                type: 'image/webp',
                lastModified: Date.now(),
              });
              resolve(webpFile);
            } else {
              reject(new Error('WebP変換に失敗しました'));
            }
          },
          'image/webp',
          quality
        );
      };
      
      img.onerror = () => {
        // メモリリーク防止
        URL.revokeObjectURL(objectUrl);
        reject(new Error('画像の読み込みに失敗しました'));
      };
      
      img.src = objectUrl;
    });
  }


  // 複数画像の一括アップロード
  static async uploadMultipleImages(
    files: File[], 
    siteId: string, 
    pageId?: string
  ): Promise<string[]> {
    try {
      const uploadPromises = files.map(file => 
        this.uploadImage(file, siteId, pageId)
      );
      
      const downloadURLs = await Promise.all(uploadPromises);
      return downloadURLs;
    } catch (error) {
      console.error('複数画像のアップロードに失敗しました:', error);
      throw error;
    }
  }

  // 画像削除
  static async deleteImage(imageUrl: string): Promise<void> {
    try {
      // URLからファイルパスを抽出
      const url = new URL(imageUrl);
      
      // /o/ の後ろの部分を抽出（pathname には ? が含まれない）
      const pathMatch = url.pathname.match(/\/o\/(.+)/);
      
      if (!pathMatch) {
        throw new Error('無効な画像URLです。パスを抽出できませんでした。');
      }
      
      const filePath = decodeURIComponent(pathMatch[1]);
      const imageRef = ref(storage, filePath);
      await deleteObject(imageRef);
    } catch (error) {
      console.error('画像の削除に失敗しました:', error);
      throw error;
    }
  }

  // サイトの画像一覧取得（URLのみ - 後方互換性のため）
  static async getSiteImages(siteId: string): Promise<string[]> {
    try {
      const siteRef = ref(storage, `sites/${siteId}/library`);
      const result = await listAll(siteRef);
      
      const imageUrls: string[] = [];
      
      // ファイルのURLを取得
      for (const itemRef of result.items) {
        const downloadURL = await getDownloadURL(itemRef);
        imageUrls.push(downloadURL);
      }
      
      return imageUrls;
    } catch (error) {
      console.error('画像一覧の取得に失敗しました:', error);
      return [];
    }
  }

  // サイトの画像ライブラリ一覧取得（メタデータ付き）
  static async getSiteImageLibrary(siteId: string): Promise<ImageMetadata[]> {
    try {
      const imageMetadataList: ImageMetadata[] = [];
      
      // 複数のパスからメディアを取得（画像と動画を分けて取得）
      const paths = [
        `sites/${siteId}/library/images`,
        `sites/${siteId}/library/videos`,
        `sites/${siteId}/library`, // 後方互換性のため
        `sites/${siteId}/images` // 後方互換性のため
      ];
      
      for (const path of paths) {
        try {
          const folderRef = ref(storage, path);
          const result = await listAll(folderRef);
          
          // 各ファイルのメタデータを取得
          for (const itemRef of result.items) {
            try {
              const downloadURL = await getDownloadURL(itemRef);
              const metadata = await getMetadata(itemRef);
              
              // ファイルタイプを判定（パスからも判定）
              const isVideo = metadata.contentType?.startsWith('video/') || path.includes('/videos/');
              
              
              imageMetadataList.push({
                url: downloadURL,
                name: itemRef.name,
                size: metadata.size,
                uploadedAt: new Date(metadata.timeCreated),
                fullPath: metadata.fullPath,
                contentType: metadata.contentType || 'image/jpeg',
                isVideo,
              });
            } catch (error) {
              console.error(`画像メタデータの取得に失敗: ${itemRef.name}`, error);
            }
          }
        } catch (error) {
          // フォルダが存在しない場合はスキップ
          console.log(`パス ${path} が見つかりません（スキップ）`);
        }
      }
      
      // アップロード日時の降順でソート
      imageMetadataList.sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
      
      return imageMetadataList;
    } catch (error) {
      console.error('画像ライブラリの取得に失敗しました:', error);
      return [];
    }
  }


  // ページの画像一覧取得
  static async getPageImages(siteId: string, pageId: string): Promise<string[]> {
    try {
      const pageRef = ref(storage, `sites/${siteId}/pages/${pageId}`);
      const result = await listAll(pageRef);
      
      const imageUrls: string[] = [];
      
      // ファイルのURLを取得
      for (const itemRef of result.items) {
        const downloadURL = await getDownloadURL(itemRef);
        imageUrls.push(downloadURL);
      }
      
      return imageUrls;
    } catch (error) {
      console.error('ページ画像一覧の取得に失敗しました:', error);
      return [];
    }
  }

  // 画像の最適化（クライアントサイド）
  static optimizeImage(file: File, maxWidth: number = 1920, quality: number = 0.8): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // アスペクト比を維持してリサイズ
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        const newWidth = img.width * ratio;
        const newHeight = img.height * ratio;
        
        canvas.width = newWidth;
        canvas.height = newHeight;
        
        // 画像を描画
        ctx?.drawImage(img, 0, 0, newWidth, newHeight);
        
        // ファイルに変換
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const optimizedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(optimizedFile);
            } else {
              reject(new Error('画像の最適化に失敗しました'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => reject(new Error('画像の読み込みに失敗しました'));
      img.src = URL.createObjectURL(file);
    });
  }

  // 動画をMP4形式に変換（サーバーサイド変換を使用）
  static async convertToMP4(file: File, siteId: string): Promise<File> {
    // 既にMP4の場合はそのまま返す
    if (file.type === 'video/mp4') {
      console.log('既にMP4形式です');
      return file;
    }
    
    // サポートされている動画形式の場合はそのまま使用
    const supportedTypes = ['video/mp4', 'video/webm', 'video/ogg'];
    if (supportedTypes.includes(file.type)) {
      console.log('サポートされている動画形式です');
      return file;
    }
    
    console.log(`動画形式 ${file.type} をサーバーサイドで変換します...`);
    
    try {
      // ファイルをBase64に変換（FileReaderを使用）
      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // data:video/avi;base64, の部分を除去
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      // サーバーサイド変換を実行
      const requestData = {
        videoData: base64Data,
        siteId: siteId,
        fileName: file.name.replace(/\.[^/.]+$/, '') + '.mp4'
      };
      
      console.log('送信データ:', {
        siteId: requestData.siteId,
        fileName: requestData.fileName,
        videoDataLength: requestData.videoData.length,
        videoDataPreview: requestData.videoData.substring(0, 50) + '...'
      });
      
      const response = await fetch('https://asia-northeast1-qwipesystem.cloudfunctions.net/convertVideo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`サーバーサイド動画変換に失敗しました: ${errorData.error || 'Unknown error'}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(`動画変換に失敗しました: ${result.error || 'Unknown error'}`);
      }

      console.log('サーバーサイド動画変換が完了しました');
      
      // Base64データをBlobに変換してFileオブジェクトを作成
      const binaryString = atob(result.convertedVideo);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const convertedBlob = new Blob([bytes], { type: 'video/mp4' });
      
      const mp4File = new File([convertedBlob], file.name.replace(/\.[^/.]+$/, '') + '.mp4', {
        type: 'video/mp4',
        lastModified: Date.now(),
      });
      
      return mp4File;
    } catch (error) {
      console.error('動画変換エラー:', error);
      throw new Error(`動画の変換に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // 一時ファイルをアップロード
  static async uploadTemporaryFile(file: File, siteId: string): Promise<string> {
    const timestamp = Date.now();
    const fileName = `temp_${timestamp}_${file.name}`;
    const path = `sites/${siteId}/temp/${fileName}`;
    
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  }

  // サーバーサイドで動画変換
  static async convertVideoOnServer(videoUrl: string, siteId: string, fileName: string): Promise<string> {
    const functionsUrl = 'https://us-central1-qwipesystem.cloudfunctions.net/convertVideo';
    
    const response = await fetch(functionsUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        videoUrl,
        siteId,
        fileName: fileName.replace(/\.[^/.]+$/, '.mp4')
      }),
    });

    if (!response.ok) {
      throw new Error('サーバーサイド動画変換に失敗しました');
    }

    const result = await response.json();
    return result.convertedUrl;
  }


  // 画像・動画アップロード（統一メソッド）
  static async uploadMedia(
    file: File, 
    siteId: string, 
    pageId?: string, 
    options?: {
      fileName?: string; // 指定されたファイル名を使用
      overwrite?: boolean; // 同名ファイルを上書きするか
      autoNumber?: boolean; // 同名ファイルがある場合に自動採番するか
      skipExistsCheck?: boolean; // true のとき fileExists を呼ばない（新規作成時の 404 防止）
    }
  ): Promise<string> {
    try {
      let uploadFile: File;
      let isVideo = false;

      // ファイルタイプを判定
      if (this.isSupportedVideoType(file)) {
        // 動画の場合
        isVideo = true;
        
        
        // 既にMP4の場合は変換をスキップ、それ以外は変換
        uploadFile = file.type === 'video/mp4' 
          ? file 
          : await this.convertToMP4(file, siteId);
      } else {
        // 画像の場合
        // WebPとGIFはそのまま、それ以外はWebPに変換
        if (file.type === 'image/webp' || file.type === 'image/gif') {
          uploadFile = file;
        } else {
          uploadFile = await this.convertToWebP(file);
        }
      }
      
      // ファイル名を決定
      let fileName: string;
      if (options?.fileName) {
        // 指定されたファイル名を使用
        fileName = options.fileName;
      } else {
        // 変換後のファイル名を使用
        fileName = uploadFile.name;
      }
      
      // 同名ファイルの処理（skipExistsCheck 時はスキップ＝新規サイト作成時の 404 を防ぐ）
      if (!options?.overwrite && !options?.skipExistsCheck) {
        // 上書きしない場合は採番を検討
        if (options?.autoNumber) {
          // 自動採番
          fileName = await this.getAvailableFileName(fileName, siteId, isVideo);
        } else {
          // 指定されたファイル名でも、最終的に存在チェックを行う
          // この時点で既に存在する場合は採番する
          if (await this.fileExists(fileName, siteId, isVideo)) {
            // 同名ファイルが存在する場合は自動採番
            fileName = await this.getAvailableFileName(fileName, siteId, isVideo);
          }
        }
      }
      // overwrite=trueの場合は上書き（何もしない）
      
      // ストレージパスを設定（画像と動画を分ける）
      const mediaType = isVideo ? 'videos' : 'images';
      const path = `sites/${siteId}/library/${mediaType}/${fileName}`;
      
      const storageRef = ref(storage, path);
      
      // ファイルをアップロード
      const snapshot = await uploadBytes(storageRef, uploadFile);
      
      // ダウンロードURLを取得
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error) {
      console.error('メディアのアップロードに失敗しました:', error);
      throw error;
    }
  }

  // 動画処理状態付きメディアアップロード
  static async uploadMediaWithStatus(file: File, siteId: string): Promise<ImageMetadata> {
    try {
      let uploadFile: File;
      let isVideo = false;
      let processingStatus: 'pending' | 'processing' | 'completed' | 'failed' = 'completed';
      let processingError: string | undefined;

      // ファイルタイプを判定
      if (this.isSupportedVideoType(file)) {
        // 動画の場合
        isVideo = true;
        
        
        // 既にMP4の場合は変換をスキップ、それ以外は変換
        if (file.type === 'video/mp4') {
          uploadFile = file;
        } else {
          processingStatus = 'processing';
          try {
            uploadFile = await this.convertToMP4(file, siteId);
            processingStatus = 'completed';
          } catch (error) {
            processingStatus = 'failed';
            processingError = error instanceof Error ? error.message : 'Unknown error';
            console.error('動画変換に失敗しました:', error);
            // 変換に失敗した場合は元のファイルを使用
            uploadFile = file;
          }
        }
      } else {
        // 画像の場合
        // WebPとGIFはそのまま、それ以外はWebPに変換
        if (file.type === 'image/webp' || file.type === 'image/gif') {
          uploadFile = file;
        } else {
          uploadFile = await this.convertToWebP(file);
        }
      }
      
      // ファイル名を生成（タイムスタンプ + 元のファイル名）
      const timestamp = Date.now();
      const fileName = `${timestamp}_${uploadFile.name}`;
      
      // ストレージパスを設定（画像と動画を分ける）
      const mediaType = isVideo ? 'videos' : 'images';
      const path = `sites/${siteId}/library/${mediaType}/${fileName}`;
      
      const storageRef = ref(storage, path);
      
      // ファイルをアップロード
      const snapshot = await uploadBytes(storageRef, uploadFile);
      
      // ダウンロードURLを取得
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      const metadata: ImageMetadata = {
        name: file.name,
        url: downloadURL,
        size: file.size,
        uploadedAt: new Date(),
        fullPath: path,
        contentType: uploadFile.type,
        isVideo,
        processingStatus,
        processingError,
      };

      return metadata;
    } catch (error) {
      console.error('メディアのアップロードに失敗しました:', error);
      throw error;
    }
  }

  // 画像アップロード（後方互換性のため残す）
  static async uploadImage(file: File, siteId: string, pageId?: string): Promise<string> {
    return this.uploadMedia(file, siteId, pageId);
  }

  // サポートされている画像形式かチェック
  static isSupportedImageType(file: File): boolean {
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    return supportedTypes.includes(file.type);
  }

  // サポートされている動画形式かチェック
  static isSupportedVideoType(file: File): boolean {
    const supportedTypes = ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/webm'];
    return supportedTypes.includes(file.type);
  }

  // サポートされているメディア形式かチェック（画像・動画両方）
  static isSupportedMediaType(file: File): boolean {
    return this.isSupportedImageType(file) || this.isSupportedVideoType(file);
  }

  // ファイルサイズチェック（管理画面用：最大100MB）
  static isValidFileSize(file: File, maxSizeMB: number = 100): boolean {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
  }

  // 同名ファイルが存在するかチェック
  static async fileExists(fileName: string, siteId: string, isVideo: boolean = false): Promise<boolean> {
    try {
      const mediaType = isVideo ? 'videos' : 'images';
      const path = `sites/${siteId}/library/${mediaType}/${fileName}`;
      const fileRef = ref(storage, path);
      
      // メタデータを取得しようとすることで、ファイルの存在を確認
      await getMetadata(fileRef);
      return true;
    } catch (error: any) {
      // ファイルが存在しない場合（404エラー）
      if (error?.code === 'storage/object-not-found') {
        return false;
      }
      // その他のエラーは再スロー
      throw error;
    }
  }

  // 採番付きファイル名を生成（例: file_1.ext, file_2.ext）
  static async getAvailableFileName(originalFileName: string, siteId: string, isVideo: boolean = false): Promise<string> {
    // ファイル名と拡張子を分離
    const lastDotIndex = originalFileName.lastIndexOf('.');
    const nameWithoutExt = lastDotIndex > 0 ? originalFileName.substring(0, lastDotIndex) : originalFileName;
    const extension = lastDotIndex > 0 ? originalFileName.substring(lastDotIndex) : '';
    
    // 元のファイル名でチェック
    if (!(await this.fileExists(originalFileName, siteId, isVideo))) {
      return originalFileName;
    }
    
    // 採番を試す（最大1000まで）
    for (let i = 1; i <= 1000; i++) {
      const numberedFileName = `${nameWithoutExt}_${i}${extension}`;
      if (!(await this.fileExists(numberedFileName, siteId, isVideo))) {
        return numberedFileName;
      }
    }
    
    // 1000まで見つからない場合はタイムスタンプを付ける
    const timestamp = Date.now();
    return `${nameWithoutExt}_${timestamp}${extension}`;
  }

  // ファイル名を変更（Firebase Storageでは直接リネームできないため、コピー→削除の処理）
  static async renameFile(oldFilePath: string, newFileName: string, siteId: string): Promise<string> {
    try {
      // 元のファイルを読み込む
      const oldFileRef = ref(storage, oldFilePath);
      const oldUrl = await getDownloadURL(oldFileRef);
      const oldMetadata = await getMetadata(oldFileRef);
      
      // ファイルをダウンロード
      const response = await fetch(oldUrl);
      const blob = await response.blob();
      
      // 新しいファイル名でパスを決定
      const isVideo = oldMetadata.contentType?.startsWith('video/') || false;
      const mediaType = isVideo ? 'videos' : 'images';
      const newPath = `sites/${siteId}/library/${mediaType}/${newFileName}`;
      const newFileRef = ref(storage, newPath);
      
      // 新しいファイル名でアップロード
      await uploadBytes(newFileRef, blob, {
        contentType: oldMetadata.contentType || 'image/webp',
      });
      
      // 元のファイルを削除
      await deleteObject(oldFileRef);
      
      // 新しいURLを返す
      return await getDownloadURL(newFileRef);
    } catch (error) {
      console.error('ファイル名の変更に失敗しました:', error);
      throw error;
    }
  }
}
